import React, { useEffect, useState } from "react";
import {
  Select,
  ErrorMessage,
  SubmitButton,
  Radio,
} from "components/Forms";
import WatchForm from "components/Forms/WatchForm";
import { useFormContext } from "react-hook-form";
import {
  MILLING_TYPES,
  FEED_TYPES,
  SPEED_TYPES,
  MATERIAL_SELECTION,
} from "./constants/constants";
import "./index.css";

const CuttingParameters = (props) => {
  const formCtx = useFormContext();
  const [image, setImage] = useState("assets/images/up-milling.png");

  const speedType = formCtx?.watch("cuttingParameters.speedTypeSelection");
  const feedType = formCtx?.watch("cuttingParameters.feedTypeSelection");
  const millingType = formCtx?.watch("cuttingParameters.millingTypeSelection");
  const diameter = formCtx?.watch("diameter");
  const widthOfCut = formCtx?.watch("cuttingParameters.widthOfCut");
  const spindleSpeed = formCtx?.watch("cuttingParameters.spindleSpeed");
  const noOfFlutes = formCtx?.watch("fluteParameters.noOfFlutes");

  const calculateCuttingSpeed = (diameter, spindleSpeed) => {
    const d = Number(diameter) || 0;
    const n = Number(spindleSpeed) || 0;

    if (d <= 0 || n <= 0) return 0;

    return (Math.PI * d * n) / 1000;
  };

  const calculateSpindleSpeed = (diameter, cuttingSpeed) => {
    const d = Number(diameter) || 0;
    const vc = Number(cuttingSpeed) || 0;

    if (d <= 0 || vc <= 0) return 0;

    return (1000 * vc) / (Math.PI * d);
  };

  const calculateFeedRate = (spindleSpeed, feedPerTooth, flutes) => {
    const n = Number(spindleSpeed) || 0;
    const fz = Number(feedPerTooth) || 0;
    const z = Number(flutes) || 0;

    if (n <= 0 || fz <= 0 || z <= 0) return 0;

    return n * fz * z;
  };

  const calculateFeedPerTooth = (feedRate, spindleSpeed, flutes) => {
    const vf = Number(feedRate) || 0;
    const n = Number(spindleSpeed) || 0;
    const z = Number(flutes) || 0;

    if (vf <= 0 || n <= 0 || z <= 0) return 0;

    return vf / (n * z);
  };


  useEffect(() => {
    if (Number(millingType) === 3) {
      const nextValue = Number(diameter) || 0;
      const currentValue = Number(formCtx.getValues("cuttingParameters.widthOfCut")) || 0;

      if (currentValue !== nextValue) {
        formCtx.setValue("cuttingParameters.widthOfCut", nextValue);
      }
    }
  }, [millingType, diameter, formCtx]);

  useEffect(() => {
    const d = Number(diameter) || 0;

    if (d <= 0) return;

    if (Number(speedType) === 1) {
      const spindleSpeed =
        Number(formCtx.getValues("cuttingParameters.spindleSpeed")) || 0;

      const nextCuttingSpeed = Number(
        calculateCuttingSpeed(d, spindleSpeed).toFixed(2)
      );

      const currentCuttingSpeed =
        Number(formCtx.getValues("cuttingParameters.cuttingSpeed")) || 0;

      if (currentCuttingSpeed !== nextCuttingSpeed) {
        formCtx.setValue("cuttingParameters.cuttingSpeed", nextCuttingSpeed);
      }
    } else {
      const cuttingSpeed =
        Number(formCtx.getValues("cuttingParameters.cuttingSpeed")) || 0;

      const nextSpindleSpeed = Math.round(
        calculateSpindleSpeed(d, cuttingSpeed)
      );

      const currentSpindleSpeed =
        Number(formCtx.getValues("cuttingParameters.spindleSpeed")) || 0;

      if (currentSpindleSpeed !== nextSpindleSpeed) {
        formCtx.setValue("cuttingParameters.spindleSpeed", nextSpindleSpeed);
      }
    }
  }, [diameter, speedType, formCtx]);

  useEffect(() => {
    const n = Number(spindleSpeed) || 0;
    const z = Number(noOfFlutes) || 0;

    if (n <= 0 || z <= 0) return;

    if (Number(feedType) === 1) {
      const currentFeedRate =
        Number(formCtx.getValues("cuttingParameters.feedRate")) || 0;

      const nextFeedPerTooth = Number(
        calculateFeedPerTooth(currentFeedRate, n, z).toFixed(5)
      );

      const currentFeedPerTooth =
        Number(formCtx.getValues("cuttingParameters.feedPerTooth")) || 0;

      if (currentFeedPerTooth !== nextFeedPerTooth) {
        formCtx.setValue("cuttingParameters.feedPerTooth", nextFeedPerTooth);
      }
    } else {
      const currentFeedPerTooth =
        Number(formCtx.getValues("cuttingParameters.feedPerTooth")) || 0;

      const nextFeedRate = Math.round(
        calculateFeedRate(n, currentFeedPerTooth, z)
      );

      const currentFeedRate =
        Number(formCtx.getValues("cuttingParameters.feedRate")) || 0;

      if (currentFeedRate !== nextFeedRate) {
        formCtx.setValue("cuttingParameters.feedRate", nextFeedRate);
      }
    }
  }, [spindleSpeed, noOfFlutes, feedType, formCtx]);

  const millingTypesRadioHandler = (value) => {
    const numericValue = Number(value);
    formCtx.setValue("cuttingParameters.millingTypeSelection", numericValue);
  
    switch (numericValue) {
      case 1:
        setImage("assets/images/up-milling.png");
        break;
      case 2:
        setImage("assets/images/down-milling.png");
        break;
      case 3:
        setImage("assets/images/slotting.png");
        break;
      default:
        setImage("assets/images/up-milling.png");
    }
  };
  
  const speedTypeRadioHandler = (value) => {
    const numericValue = Number(value);
    formCtx.setValue("cuttingParameters.speedTypeSelection", numericValue);
  };
  
  const feedTypeRadioHandler = (value) => {
    const numericValue = Number(value);
    formCtx.setValue("cuttingParameters.feedTypeSelection", numericValue);
  };

  const handleNumberChange = (name, value, min = 0, max = Infinity) => {
    let parsedValue = parseFloat(value);

    if (isNaN(parsedValue)) {
      parsedValue = 0;
    }

    if (parsedValue < min) {
      parsedValue = min;
    }

    if (parsedValue > max) {
      parsedValue = max;
    }

    formCtx.setValue(name, parsedValue);


    const currentDiameter = Number(formCtx.getValues("diameter")) || 0;
    const currentSpindleSpeed =
      Number(formCtx.getValues("cuttingParameters.spindleSpeed")) || 0;
    const currentFlutes =
      Number(formCtx.getValues("fluteParameters.noOfFlutes")) || 0;

    if (name === "cuttingParameters.spindleSpeed") {
      const nextCuttingSpeed = Number(
        calculateCuttingSpeed(currentDiameter, parsedValue).toFixed(2)
      );
      const currentCuttingSpeed =
        Number(formCtx.getValues("cuttingParameters.cuttingSpeed")) || 0;

      if (currentCuttingSpeed !== nextCuttingSpeed) {
        formCtx.setValue("cuttingParameters.cuttingSpeed", nextCuttingSpeed);
      }

      const currentFeedRate =
        Number(formCtx.getValues("cuttingParameters.feedRate")) || 0;
      const nextFeedPerTooth = Number(
        calculateFeedPerTooth(currentFeedRate, parsedValue, currentFlutes).toFixed(5)
      );
      const currentFeedPerTooth =
        Number(formCtx.getValues("cuttingParameters.feedPerTooth")) || 0;

      if (
        currentFlutes > 0 &&
        currentFeedPerTooth !== nextFeedPerTooth
      ) {
        formCtx.setValue("cuttingParameters.feedPerTooth", nextFeedPerTooth);
      }
    }

    if (name === "cuttingParameters.cuttingSpeed") {
      const nextSpindleSpeed = Math.round(
        calculateSpindleSpeed(currentDiameter, parsedValue)
      );
      const currentSpindleSpeedValue =
        Number(formCtx.getValues("cuttingParameters.spindleSpeed")) || 0;

      if (currentSpindleSpeedValue !== nextSpindleSpeed) {
        formCtx.setValue("cuttingParameters.spindleSpeed", nextSpindleSpeed);
      }

      const currentFeedPerTooth =
        Number(formCtx.getValues("cuttingParameters.feedPerTooth")) || 0;
      const nextFeedRate = Math.round(
        calculateFeedRate(nextSpindleSpeed, currentFeedPerTooth, currentFlutes)
      );
      const currentFeedRate =
        Number(formCtx.getValues("cuttingParameters.feedRate")) || 0;

      if (currentFlutes > 0 && currentFeedRate !== nextFeedRate) {
        formCtx.setValue("cuttingParameters.feedRate", nextFeedRate);
      }
    }

    if (name === "cuttingParameters.feedRate") {
      const nextFeedPerTooth = Number(
        calculateFeedPerTooth(
          parsedValue,
          currentSpindleSpeed,
          currentFlutes
        ).toFixed(5)
      );

      const currentFeedPerTooth =
        Number(formCtx.getValues("cuttingParameters.feedPerTooth")) || 0;

      if (currentFeedPerTooth !== nextFeedPerTooth) {
        formCtx.setValue("cuttingParameters.feedPerTooth", nextFeedPerTooth);
      }
    }

    if (name === "cuttingParameters.feedPerTooth") {
      const nextFeedRate = Math.round(
        calculateFeedRate(currentSpindleSpeed, parsedValue, currentFlutes)
      );

      const currentFeedRate =
        Number(formCtx.getValues("cuttingParameters.feedRate")) || 0;

      if (currentFeedRate !== nextFeedRate) {
        formCtx.setValue("cuttingParameters.feedRate", nextFeedRate);
      }
    }


  };

  const renderNumberInput = ({
    name,
    label,
    unit,
    min = 0,
    max = Infinity,
    step = "0.1",
    value,
    disabled = false,
  }) => (
    <div className="input-wrapper d-flex flex-row align-items-center mb-4">
      <div className="d-flex flex-column col-3 align-items-center">
        <label
          className="col-form-label"
          style={{ width: props.windowWidth < 994 && "100px" }}
        >
          {label}
        </label>
      </div>

      <div className="d-flex col-9 align-items-center">
        <div className="d-flex flex-column col-8">
          <input
            type="number"
            step={step}
            min={min}
            max={max}
            value={value ?? 0}
            disabled={disabled}
            className="form-control"
            style={{ maxWidth: "220px" }}
            onChange={(e) =>
              handleNumberChange(name, e.target.value, min, max)
            }
            onBlur={(e) => {
              if (e.target.value === "") {
                formCtx.setValue(name, 0);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          />
          <small className="text-muted mt-1">
            Range: {min} - {max === Infinity ? "No limit" : max} {unit}
          </small>
          <div className="mt-2">
            <ErrorMessage name={name} />
          </div>
        </div>

        <div className="ml-3" style={{ minWidth: "70px", fontWeight: 500 }}>
          {unit}
        </div>
      </div>
    </div>
  );

  return (
    <div className="d-flex flex-column">
      {props.windowWidth <= 994 && (
        <div className="align-self-center col-5 ml-5">
          <img
            style={{
              maxHeight: "350px",
              width: "100%",
              marginTop: props.windowWidth <= 994 ? "10%" : "50%",
            }}
            src={image}
            alt="Milling process"
          />
        </div>
      )}

      <div
        className={
          props.windowWidth <= 994
            ? "d-flex justify-content-center"
            : "d-flex justify-content-start"
        }
      >
        <div
          className={
            props.windowWidth <= 994
              ? "cutting-parameters-input-container col-12 p-0"
              : "cutting-parameters-input-container col-8 pl-4 pr-4"
          }
        >
          <div className="d-flex justify-content-center mt-1 ml-5">
            <h3 className="text-center mb-5">Cutting Parameter Selection</h3>
          </div>

          <div className="inputs">
            <div className="input-wrapper d-flex flex-row align-items-center pb-3">
              <div className="d-flex flex-row col-3">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Milling Type
                </label>
              </div>

              <div
                className="input-wrapper d-flex col-9 justify-content-start align-items-center"
                style={{ marginLeft: props.windowWidth < 994 && "40px" }}
              >
                <div className="d-flex col-12 flex-row">
                  <div className="btn-group-toggle w-100" data-toggle="buttons">
                    <Radio
                      onChange={(value) => millingTypesRadioHandler(value)}
                      name="cuttingParameters.millingTypeSelection"
                      options={MILLING_TYPES}
                    />
                  </div>
                  <ErrorMessage name="cuttingParameters.millingTypeSelection" />
                </div>
              </div>
            </div>

            <WatchForm name="fluteLength">
              {(fluteLength) =>
                renderNumberInput({
                  name: "cuttingParameters.depthOfCut",
                  label: "Depth of Cut (mm):",
                  unit: "mm",
                  min: 0,
                  max: fluteLength || 0,
                  step: "0.1",
                  value: formCtx.watch("cuttingParameters.depthOfCut"),
                })
              }
            </WatchForm>

            <WatchForm name="diameter">
              {(currentDiameter) =>
                renderNumberInput({
                  name: "cuttingParameters.widthOfCut",
                  label: "Width of Cut (mm):",
                  unit: "mm",
                  min: 0,
                  max: currentDiameter || 0,
                  step: "0.1",
                  value: Number(millingType) === 3 ? currentDiameter || 0 : widthOfCut,
                  disabled: Number(millingType) === 3,
                })
              }
            </WatchForm>

            <div className="input-wrapper d-flex flex-row align-items-center pb-3">
              <div className="d-flex flex-row col-3">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Speed Type
                </label>
              </div>

              <div
                className="input-wrapper d-flex col-9 justify-content-start align-items-center"
                style={{ marginLeft: props.windowWidth < 994 && "40px" }}
              >
                <div className="d-flex col-12 flex-row">
                  <div className="btn-group-toggle h-50" data-toggle="buttons">
                    <Radio
                      onChange={(value) => speedTypeRadioHandler(value)}
                      name="cuttingParameters.speedTypeSelection"
                      options={SPEED_TYPES}
                    />
                  </div>
                </div>
              </div>
            </div>

            {Number(speedType) === 1
              ? renderNumberInput({
                  name: "cuttingParameters.spindleSpeed",
                  label: "Spindle Speed (RPM):",
                  unit: "RPM",
                  min: 0,
                  max: 40000,
                  step: "1",
                  value: formCtx.watch("cuttingParameters.spindleSpeed"),
                })
              : renderNumberInput({
                  name: "cuttingParameters.cuttingSpeed",
                  label: "Cutting Speed (m/min):",
                  unit: "m/min",
                  min: 0,
                  max: 15000,
                  step: "0.1",
                  value: formCtx.watch("cuttingParameters.cuttingSpeed"),
                })}

            <div className="input-wrapper d-flex flex-row align-items-center pb-3">
              <div className="d-flex flex-row col-3">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Feed Type
                </label>
              </div>

              <div
                className="input-wrapper d-flex col-9 justify-content-start align-items-center"
                style={{ marginLeft: props.windowWidth < 994 && "40px" }}
              >
                <div className="d-flex col-12 flex-row">
                  <div className="btn-group-toggle h-50" data-toggle="buttons">
                    <Radio
                      onChange={(value) => feedTypeRadioHandler(value)}
                      name="cuttingParameters.feedTypeSelection"
                      options={FEED_TYPES}
                    />
                  </div>
                </div>
              </div>
            </div>

            {Number(feedType) === 1
              ? renderNumberInput({
                  name: "cuttingParameters.feedRate",
                  label: "Feed Rate (mm/min):",
                  unit: "mm/min",
                  min: 0,
                  max: 40000,
                  step: "1",
                  value: formCtx.watch("cuttingParameters.feedRate"),
                })
              : renderNumberInput({
                  name: "cuttingParameters.feedPerTooth",
                  label: "Feed Per Tooth (mm/tooth):",
                  unit: "mm/tooth",
                  min: 0,
                  max: 1,
                  step: "0.0001",
                  value: formCtx.watch("cuttingParameters.feedPerTooth"),
                })}

            <div className="d-flex ml-4 justify-content-center">
              <div className="w-50 ml-5 mb-2">
                <ErrorMessage name="cuttingParameters.feedTypeSelection" />
              </div>
            </div>

            <div className="input-wrapper d-flex flex-row align-items-center mb-3">
              <div className="d-flex flex-column col-3 align-items-center">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Workpiece Material:
                </label>
              </div>

              <div className="d-flex col-10">
                <Select
                  name="cuttingParameters.materialSelection"
                  options={MATERIAL_SELECTION}
                  className="form-control ml-3 w-75"
                  placeholder="Shape"
                />
              </div>
            </div>
          </div>

          <div
            style={{ marginLeft: "5em" }}
            className="d-flex align-items-center justify-content-center"
          >
            <SubmitButton
              type="submit"
              className="btn btn-info waves-effect waves-light"
              style={{
                width: "200px",
                backgroundColor: "#3398DB",
                color: "#fff",
              }}
              disabled={props.loading}
            >
              Submit
            </SubmitButton>
          </div>
        </div>

        {props.windowWidth > 994 && (
          <div className="col-4">
            <img
              style={{
                maxHeight: "25em",
                marginTop: props.windowWidth <= 994 ? "10%" : "65%",
              }}
              src={image}
              alt="Milling process"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CuttingParameters;